import React, { useState, useEffect } from "react";
import { Rnd } from "react-rnd";

export interface TableType {
  id: string;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  imageUrl: string;
  realDimensions?: { width: number; height: number };
  rotation?: number;
  flipX?: boolean;
  flipY?: boolean;
}

export interface DraggableTableIconProps {
  table: TableType;
  onDragEnd?: (id: string, x: number, y: number) => void;
  onResize?: (id: string, width: number, height: number) => void;
  onRotate?: (id: string, rotation: number) => void;
  highlight?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>, tableId: string) => void;
  disableDragResize?: boolean;
}

const GRID_SIZE = 20;
const REAL_GRID_SIZE = 50;

function DraggableTableIcon({
  table,
  onDragEnd,
  onResize,
  onRotate,
  highlight,
  onClick,
  disableDragResize,
}: DraggableTableIconProps) {
  const pixelsPerCm = GRID_SIZE / REAL_GRID_SIZE;
  const [position, setPosition] = useState({ x: table.x, y: table.y });
  const [displayWidth, setDisplayWidth] = useState(table.width);
  const [displayHeight, setDisplayHeight] = useState(table.height);
  const [isHovering, setIsHovering] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [startAngle, setStartAngle] = useState(0);

  useEffect(() => {
    setPosition({ x: table.x, y: table.y });
    setDisplayWidth(table.width);
    setDisplayHeight(table.height);
  }, [table.x, table.y, table.width, table.height]);

  const handleDragStop = (e: any, d: any) => {
    setPosition({ x: d.x, y: d.y });
    if (onDragEnd) onDragEnd(table.id, d.x, d.y);
  };

  const handleResizeStop = (e: any, direction: any, ref: any, delta: any, position: any) => {
    setDisplayWidth(parseFloat(ref.style.width));
    setDisplayHeight(parseFloat(ref.style.height));
    setPosition(position);
    if (onResize) onResize(table.id, parseFloat(ref.style.width), parseFloat(ref.style.height));
    if (onDragEnd) onDragEnd(table.id, position.x, position.y);
  };

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (onClick) onClick(e, table.id);
  };

  // 计算两点之间的角度（弧度）
  const getAngle = (cx: number, cy: number, px: number, py: number) => {
    const x = px - cx;
    const y = py - cy;
    return Math.atan2(y, x);
  };

  // 旋转控制器的拖动开始
  const handleRotateStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    // 计算桌子中心点
    const tableRect = e.currentTarget.parentElement?.getBoundingClientRect();
    if (!tableRect) return;
    
    const centerX = tableRect.left + tableRect.width / 2;
    const centerY = tableRect.top + tableRect.height / 2;
    
    // 计算初始角度
    const initialAngle = getAngle(centerX, centerY, e.clientX, e.clientY);
    setStartAngle(initialAngle - (table.rotation || 0) * Math.PI / 180);
    
    setIsRotating(true);
  };

  // 处理旋转过程
  useEffect(() => {
    if (!isRotating) return;

    const handleRotateMove = (e: MouseEvent) => {
      const tableElement = document.getElementById(`table-${table.id}`);
      if (!tableElement) return;
      
      const tableRect = tableElement.getBoundingClientRect();
      const centerX = tableRect.left + tableRect.width / 2;
      const centerY = tableRect.top + tableRect.height / 2;
      
      // 计算当前角度
      const currentAngle = getAngle(centerX, centerY, e.clientX, e.clientY);
      
      // 计算旋转的角度（度数）
      let rotation = ((currentAngle - startAngle) * 180 / Math.PI) % 360;
      
      // 确保角度在0-360之间
      if (rotation < 0) rotation += 360;
      
      // 更新旋转角度
      if (onRotate) onRotate(table.id, rotation);
    };

    const handleRotateEnd = () => {
      setIsRotating(false);
    };

    document.addEventListener('mousemove', handleRotateMove);
    document.addEventListener('mouseup', handleRotateEnd);

    return () => {
      document.removeEventListener('mousemove', handleRotateMove);
      document.removeEventListener('mouseup', handleRotateEnd);
    };
  }, [isRotating, table.id, startAngle, onRotate]);
  
  const transform = `rotate(${table.rotation || 0}deg) 
                  scaleX(${table.flipX ? -1 : 1}) 
                  scaleY(${table.flipY ? -1 : 1})`;

  return (
    <Rnd
      id={`table-${table.id}`}
      size={{ width: displayWidth, height: displayHeight }}
      position={position}
      onDragStop={handleDragStop}
      onResizeStop={handleResizeStop}
      onClick={handleClick}
      lockAspectRatio={false}
      dragGrid={[GRID_SIZE, GRID_SIZE]}
      resizeGrid={[GRID_SIZE, GRID_SIZE]}
      disableDragging={disableDragResize}
      enableResizing={!disableDragResize}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      style={{
        border: highlight ? "3px solid #ff637d" : "none",
        cursor: "pointer",
        transform: transform,
      }}
    >
      <div
        className="table-content draggable-table"
        data-id={table.id}
        style={{
          width: "100%",
          height: "100%",
          transform: `rotate(${table.rotation || 0}deg)`,
          transformOrigin: 'center center',
          backgroundImage: `url(${table.imageUrl})`,
          backgroundSize: "100% 100%",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            color: "#333",
            fontSize: "10px",
          }}
        >
          {table.name}
        </div>
        
        {/* 旋转控制器 */}
        {isHovering && !disableDragResize && (
          <div 
            className="rotation-handle"
            onMouseDown={handleRotateStart}
            style={{
              position: 'absolute',
              top: '-20px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#4c82ff',
              cursor: 'grab',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 0 5px rgba(0,0,0,0.3)',
              zIndex: 100
            }}
          >
            <span style={{ color: 'white', fontSize: '12px' }}>⟳</span>
          </div>
        )}
      </div>
    </Rnd>
  );
}

export default DraggableTableIcon;
